"use client";
import { usePrice } from "@/hooks";
import { useGetHostingPackagesQuery } from "@/store";
import { setToLocale } from "@/utils";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ToggleGroup } from "./ToggleGroup";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const HostingPackages = () => {
  const formatAmount = usePrice();  
  const t = useTranslations();
  const frequencies = [
    { id: "monthly", text: t("monthly"), priceSuffix: `/${t("month")}` },
    { id: "annually", text: t("annualy"), priceSuffix: `/${t("year")}` },
  ];

  const navigate = useRouter();
  const {
    formType,
    formType: { hostingFrequency },
  } = useSelector((state) => state.common);

  // hostingFrequency
  const { data } = useGetHostingPackagesQuery();
  const [activeId, setActiveId] = useState(data?.body?.hosting_packages[2].id);

  useEffect(() => {
    if (data?.body?.hosting_packages) {
      setActiveId(data.body.hosting_packages[2].id);
    }
  }, [data]);

  const active = (id) => {
    setActiveId(id);
  };

  const suffixHandler = () =>
    frequencies.find((item) => item.id === formType["hostingFrequency"])
      ?.priceSuffix;

  const getStarted = ({
    package_options,
    id,
    cost,
    cost_yearly,
    packageName,
  }) => {
    const options = {};
    options["name"] = packageName;
    package_options?.map(({ value, name }) => {
      switch (name) {
        case t("storageOption"):
          return (options["storage"] = value);
        case t("bandwidth"):
          return (options["bandwidth"] = value);
        case t("sslAside"):
          return (options["ssl"] = value);
        case t("emailAside"):
          return (options["email"] = value);
        case t("website"):
          return (options["website"] = value);
        case t("includedDomain"):
          return (options["domain"] = value);
      }
    });
    setToLocale({
      key: "whPackage",
      value: { options, id, cost, cost_yearly },
    });
    navigate.push("/web-hosting/purchase");
  };
  return (
    <div className="bg-white sm:py-16 py-8">
      <div className="mx-auto max-w-7xl text-center">
        <div className="flex  flex-col md:flex-row items-center justify-between lsm:px-12 px-4 md:mb-16 lsm:mb-8 mb-4">
          <div className="lg:w-1/2 flex flex-col max-w-[1296px]  gap-8">
            <span className="md:text-[42px] sm:text-3xl text-xl md:leading-[42px] sm:leading-[30px] leading-5 tracking-[0.1px] text-mainDarkPurple font-bold text-start">
              {t("choose_hosting")}
            </span>
            <span className="sm:text-xl text-base sm:tracking-[0.1px] sm:font-medium text-mainLightPurpleText text-start">
              {t("goodStart")}
            </span>
          </div>
          <div className="lg:w-1/2 pt-10">
            <ToggleGroup toggleItems={frequencies} isCenter={false} sm type="hostingFrequency" />
          </div>
        </div>
        <div className="isolate relative mx-auto grid p-4 grid-cols-1 lmd:gap-3 gap-4 lsm:grid-cols-1 xmd:grid-cols-2 lmd:grid-cols-3 lg:mx-0 lg:max-w-none ">
          {data?.body?.hosting_packages?.map(
            (
              { id, cost, cost_yearly, package_options, name, description },
              idx
            ) => {
              if (idx === 0) return;
              return (
                <div key={id} className={`${idx === 2 ? 'lmd:top-2 top-0 lmd:scale-105 scale-100 z-20' : '' } relative`}>
                  {idx === 2 ? (
                    <div className="absolute right-4 top-4 text-xs px-3 py-2 bg-white rounded-2xl font-bold text-mainLightPurple">
                      <span> {t("mostPopular")} </span>
                    </div>
                  ) : null}
                  <div
                    className={classNames(
                      activeId === id
                        ? "ring-2 ring-mainDarkPurpleRing shadow-xl shadow-mainDarkPurpleRing/50"
                        : "ring-1 ring-gray-100 shadow-md",
                      "rounded-lg sm:p-6 md:p-4 p-2 cursor-pointer hover:ring-2 hover:ring-mainDarkPurpleRing transition bg-[#FAFBFF] shadow-xl "
                    )}
                    onClick={() => active(id)}
                  >

                    <div className="sm:mt-6 mt-2 flex flex-col gap-x-1 px-1">
                      <div className="flex px-1 sm:py-0 py-2">
                        <div className="sm:text-4xl text-2xl font-bold tracking-tight text-mainDarkPurple">
                          {hostingFrequency === frequencies[0].id
                            ? formatAmount(cost)
                            : formatAmount(cost_yearly)}
                        </div>
                        <div className="text-sm font-semibold leading-[3rem] text-mainDarkPurple ml-2 sm:mt-1">
                          {suffixHandler()}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white px-2 py-3 rounded-xl">
                      <div className="flex items-center px-1 gap-x-4 relative">
                        <div id={id} className={classNames( activeId === id ? "text-mainDarkPurple" : "text-mainDarkPurple", "sm:text-2xl text-lg font-bold leading-8 text-left" )} >
                          {name}
                        </div>
                      </div>
                      {/* <p className=" text-base font-semibold leading-6 text-mainDarkPurple text-center">
                        {description}
                      </p> */}

                      <ul
                        role="list"
                        className="mt-8 xl:mt-8 space-y-3 text-sm leading-6 text-mainDarkPurpleText "
                      >
                        {package_options.map(
                          ({ id, name, type, value }, idxPackages) => {
                            if (idxPackages > 10) return;
                            return (
                              <li key={id} className="flex gap-x-3">
                                <CheckIcon
                                  className="h-6 w-5 flex-none text-mainGreen"
                                  aria-hidden="true"
                                />
                                <div className="text-left">
                                  <span className="font-bold font-sans">
                                    {value}{" "}
                                  </span>
                                  {name}
                                </div>
                              </li>
                            );
                          }
                        )}
                      </ul>
                    </div>

                    <div
                      onClick={() =>
                        getStarted({ package_options, id: id, cost, cost_yearly, packageName: name, })
                      }
                      aria-describedby={id}
                      className={classNames(
                        activeId === id
                          ? "bg-mainLightPurple text-white shadow-sm hover:bg-white hover:text-mainLightPurple "
                          : "text-mainLightPurple bg-white  hover:ring-blue-300 hover:bg-mainLightPurple hover:text-white",
                        "mt-6 block rounded-3xl py-2 px-3 text-center text-sm font-semibold leading-6 border border-mainLightPurple transition-all duration-200"
                      )}
                    >
                      {t("getStarted")}
                    </div>


                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

export default HostingPackages;
